import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationItem from './NotificationItem';
import { useNotifications } from '../contexts/NotificationContext';
import { A, fonts } from '../lib/theme';

const NotificationDropdown = ({ isOpen, onClose, anchorRef }) => {
    const { notifications, unreadCount, markAllAsRead, clearAll, fetchNotifications, markAsRead } = useNotifications();
    const [position, setPosition] = useState({ top: 0, right: 0, width: 340 });

    const updatePosition = useCallback(() => {
        if (anchorRef?.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const width = Math.min(340, window.innerWidth - 24);
            setPosition({ top: rect.bottom + 8, right: Math.max(12, window.innerWidth - rect.right), width });
        }
    }, [anchorRef]);

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            fetchNotifications();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        }
    }, [isOpen, updatePosition, fetchNotifications]);

    if (!isOpen) return null;

    return createPortal(
        <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1040 }} onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.97 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                style={{
                    position: 'fixed',
                    top: position.top,
                    right: position.right,
                    width: position.width,
                    maxHeight: 500,
                    background: A.bg,
                    border: `1px solid ${A.border}`,
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(28,16,8,0.14), 0 2px 8px rgba(0,0,0,0.06)',
                    zIndex: 1050,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '14px 18px 12px',
                    borderBottom: `1px solid ${A.border}`,
                    background: A.surface,
                    flexShrink: 0,
                }}>
                    <p style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.18em',
                        color: A.muted, textTransform: 'uppercase', marginBottom: 6
                    }}>
                        NOTIFICATIONS
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <h3 style={{
                                fontFamily: fonts.display, fontSize: 17, fontWeight: 700,
                                color: A.dark, margin: 0
                            }}>Notifications</h3>
                            {unreadCount > 0 && (
                                <span style={{
                                    background: A.crimson, color: '#fff',
                                    fontSize: 10, fontWeight: 700,
                                    borderRadius: 20, padding: '1px 7px',
                                    letterSpacing: '0.04em'
                                }}>{unreadCount}</span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                style={{
                                    background: 'transparent',
                                    border: `1px solid ${A.gold}`,
                                    borderRadius: 6, padding: '3px 10px',
                                    color: A.gold, fontSize: 10, fontWeight: 700,
                                    letterSpacing: '0.06em', cursor: 'pointer',
                                    textTransform: 'uppercase', transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = A.gold; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = A.gold; }}
                            >
                                Mark all read
                            </button>
                        )}
                    </div>
                </div>

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <AnimatePresence initial={false}>
                        {notifications.length > 0 ? (
                            notifications.map(notification => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onRead={markAsRead}
                                />
                            ))
                        ) : (
                            <div style={{
                                padding: '40px 20px', textAlign: 'center',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
                            }}>
                                <span style={{ fontSize: 32 }}>📬</span>
                                <p style={{ fontFamily: fonts.display, fontSize: 14, color: A.dark, marginBottom: 2 }}>
                                    All caught up!
                                </p>
                                <p style={{ fontSize: 11, color: A.muted }}>
                                    No new notifications
                                </p>
                            </div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                    <div style={{
                        padding: '8px 12px',
                        borderTop: `1px solid ${A.border}`,
                        background: A.surface,
                        flexShrink: 0,
                    }}>
                        <button
                            onClick={clearAll}
                            style={{
                                width: '100%', background: 'transparent',
                                border: `1px solid ${A.border}`, borderRadius: 6,
                                padding: '6px 0', color: A.muted, fontSize: 11,
                                fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer',
                                textTransform: 'uppercase', transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = A.crimson; e.currentTarget.style.color = A.crimson; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = A.border; e.currentTarget.style.color = A.muted; }}
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </motion.div>
        </>,
        document.body
    );
};

export default NotificationDropdown;
